/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useContext, useState, useRef } from 'react';
import classnames from 'classnames';
import { InView } from 'react-intersection-observer';
import { uniq, sortBy } from 'lodash';

import { Button, Throbber as ThrobberComponent } from '@lt/components';
import { useTranslation } from '@lt/localization';

import { isSSR } from 'src/helpers/ssr';

// Components:
import {
  HotelListBanner,
  HOTEL_LIST_BANNER_EXPLORE,
} from 'src/components/HotelListBanner';
import HotelsLoadingPreview from 'src/components/Hotel/HotelsLoadingPreview';
import styles from './hotelsContainer.module.css';
import HotelCard from './components/HotelCard';
import EmptyHotels from './components/EmptyHotels';

// Context:
import { MobileContext } from '../../domain/context/MobileContext';

import type { IHotelsContainer, THotelCoordinate } from './types';

export const HotelsContainer = (props: IHotelsContainer): JSX.Element => {
  const {
    className,
    hotels,
    buildUrl,
    hotelsCount,
    mapExpanded,
    fallbackSearch,
    showMore,
    setCurrentVisibleHotels,
    hotelHover,
    awaitFiltration,
    awaitPagination,
    modifierStyles,
    isWhiteLabel,
    isWebview,
    isHotelOnly,
    currencyCode,
    activateCashbackForPartner,
    isLiveSearchAllowed,
    getImages,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
  } = props;

  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const timerRef = useRef<null | NodeJS.Timeout>(null);

  type CustomCSSProperties = {
    '--textColor'?: string;
  };

  type StyleProps = React.CSSProperties & CustomCSSProperties;

  // стейт массива индексов отелей, находящихся в зоне видимости viewport
  const [visibleHotels, setVisibleHotels] = useState<number[]>([]);
  const [visibleAdfox, setVisibleAdfox] = useState(false);

  useEffect(() => {
    // При открытии explore первый раз мы не рендерим баннер adfox сразу, тк в window не успевает загрузиться поле adfoxCode

    if (!window?.Ya?.adfoxCode) {
      timerRef.current = setTimeout(() => {
        setVisibleAdfox(true);
      }, 3000);
    } else {
      setVisibleAdfox(true);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      if (!isSSR) window.addEventListener('popstate', buildUrl);
    }
    return () => {
      if (isMobile) {
        if (!isSSR) window.removeEventListener('popstate', buildUrl);
      }
    };
  }, [buildUrl, isMobile]);

  useEffect(() => {
    if (awaitFiltration) {
      setCurrentVisibleHotels(visibleHotels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitFiltration]);

  const [hotelCoordinates, setHotelCoordinates] = useState<THotelCoordinate>();
  useEffect(() => {
    let timeout;
    if (hotelCoordinates) {
      timeout = setTimeout(() => {
        hotelHover(hotelCoordinates);
      }, 600);
    }

    return () => (timeout ? clearTimeout(timeout) : undefined);
  }, [hotelCoordinates, hotelHover]);

  if ((!Array.isArray(hotels) || !hotels.length) && !awaitFiltration) {
    return <EmptyHotels isMobile={isMobile} />;
  }

  const handleClickShowMore = () => {
    showMore();
  };
  const displayShowMoreBtn = hotelsCount > hotels.length && !fallbackSearch;

  const handleBlur = () => setHotelCoordinates(undefined);
  const handleHover = (hotelId, index) => {
    const hoveredHotel = hotels.find((h) => h.hotel.id === hotelId);

    if (hoveredHotel) {
      const {
        hotel: { id, long, lat },
      } = hoveredHotel;

      if (long && lat) {
        const activeHotelCoordinates: THotelCoordinate = {
          index,
          id,
          coordinates: [long, lat],
        };

        setHotelCoordinates(activeHotelCoordinates);
      }
    }
  };

  const handleHotelVisibility = (index: number, inView: boolean) => {
    let newVisibleHotels = [...visibleHotels];

    if (inView) newVisibleHotels.push(index);
    else newVisibleHotels = newVisibleHotels.filter((n) => n !== index);

    // сортируем по возрастанию и убираем дубли
    setVisibleHotels(uniq(sortBy([...newVisibleHotels])));
  };

  const handleImagesLoad = (hotelId) => {
    if (!isLiveSearchAllowed) return;

    getImages(hotelId);
  };

  return (
    <>
      <div
        className={classnames(styles.hotelList, className, {
          [styles.hotelListMapExpandedDesktop]: mapExpanded && !isMobile,
        })}
      >
        {awaitFiltration ? (
          <HotelsLoadingPreview
            visibleLimit={hotels.length}
            smallCard={isMobile}
          />
        ) : (
          hotels.map((hotel, index) => (
            <>
              {visibleAdfox && (
                <HotelListBanner
                  className={styles.hotelListBanner}
                  index={index}
                  type={HOTEL_LIST_BANNER_EXPLORE}
                  isWhitelabel={isWhiteLabel}
                  isMobile={isMobile}
                />
              )}
              <div
                className={styles.hotelCardWrapper}
                role="button"
                tabIndex={0}
                key={hotel.hotel.id}
                onClick={
                  isMobile
                    ? () => handleHover(hotel.hotel.id, index)
                    : () => undefined
                }
                onMouseEnter={() => handleHover(hotel.hotel.id, index)}
                onMouseLeave={handleBlur}
              >
                <InView
                  onChange={(inView) => handleHotelVisibility(index, inView)}
                >
                  <HotelCard
                    isMapCard={false}
                    currencyCode={currencyCode}
                    isFlexDates /* on Explore always show extended card with nights */
                    isHotelOnly={isHotelOnly}
                    isMobile={isMobile || mapExpanded}
                    openInCurrentTab={isWebview}
                    isCashbackEnable={activateCashbackForPartner}
                    getImages={() => handleImagesLoad(hotel.hotel.id)}
                    cashbackTooltipText={cashbackTooltipText}
                    labelColor={labelColor}
                    textColor={textColor}
                    cashbackIconWebp={cashbackIconWebp}
                    cashbackIconPng={cashbackIconPng}
                    cashbackLabel={cashbackLabel}
                    {...hotel}
                  />
                </InView>
              </div>
            </>
          ))
        )}
      </div>

      {displayShowMoreBtn ? (
        <Button
          className={classnames(styles.button, {
            [styles.buttonMobile]: isMobile,
            [styles.buttonTextColor]: modifierStyles?.textColor,
          })}
          style={
            {
              '--textColor': modifierStyles?.textColor,
            } as StyleProps
          }
          type="button"
          onClick={handleClickShowMore}
          isMobile={isMobile}
          textColor={modifierStyles.textColor}
        >
          {awaitPagination ? (
            <ThrobberComponent size={18} color="#007aff" />
          ) : (
            t('explore.hotels.showMore')
          )}
        </Button>
      ) : null}
    </>
  );
};
